import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

// ── תפילה אחרי ספירה — Modern ────────────────────────────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
      <Typography sx={{
        fontSize: '0.6rem',
        color: '#94a3b8',
        letterSpacing: '0.12em',
        fontFamily: 'Frank Ruhl Libre, serif',
        whiteSpace: 'nowrap',
      }}>
        {section.title}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
    </Box>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.65rem',
        color: '#475569',
        lineHeight: 1.9,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

// ── תבנית מודרנית ─────────────────────────────────────────────────────────────
const Modern = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText, sefirahName, omerDayHebrew,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showPasuk, pasukText,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  const bg = bgColor || '#ffffff';

  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bg,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
      }}>
        <Typography sx={{ color: '#94a3b8', fontFamily: 'Frank Ruhl Libre, serif', fontSize: '0.9rem' }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '14px 18px',
      direction: 'rtl',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 },
    }}>
      {showTitle && (
        <Typography sx={{
          fontSize: '0.65rem',
          color: '#94a3b8',
          letterSpacing: '0.15em',
          mb: 1,
          fontFamily: 'Frank Ruhl Libre, serif',
        }}>
          {title}
        </Typography>
      )}

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <Box sx={{
          mb: 1.5, px: 2, py: 0.75,
          background: '#f0f4ff',
          border: '1px solid #c7d2fe',
          borderRadius: 1.5,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: '#1e3a5f',
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {previewCountdown}
          </Typography>
        </Box>
      )}

      {/* לשם ייחוד */}
      {showLeshemYichud && (
        <Box sx={{
          mb: 1.5,
          px: 2, py: 1,
          background: '#f8fafc',
          borderRadius: 1.5,
          border: '1px solid #e2e8f0',
          width: '100%',
          maxWidth: 420,
        }}>
          {leshemYichudLines.map((line, i) => (
            <Typography key={i} sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '0.58rem',
              color: '#64748b',
              lineHeight: 1.9,
              textAlign: 'center',
              direction: 'rtl',
            }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {/* פסוק */}
      {showPasuk !== 'none' && pasukText && (
        <Box sx={{ mb: 1.5, px: 1 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.62rem',
            color: '#475569',
            lineHeight: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            direction: 'rtl',
          }}>
            {pasukText}
          </Typography>
        </Box>
      )}

      {/* ברכה */}
      {showBracha && (
        <Box sx={{
          mb: 1.5,
          px: 2, py: 1,
          background: '#f1f5f9',
          borderRadius: 1.5,
          width: '100%',
          maxWidth: 420,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: '#1e293b',
            lineHeight: 2,
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {brachaText}
          </Typography>
        </Box>
      )}

      {/* מספר בולט — לוח גדול */}
      <Box sx={{ position: 'relative', my: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{
          fontSize: 'clamp(5rem, 18vw, 9rem)',
          fontWeight: 900,
          color: '#e8edf3',
          lineHeight: 1,
          userSelect: 'none',
          position: 'absolute',
        }}>
          {omerDay}
        </Typography>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(2rem, 7vw, 3.5rem)',
          fontWeight: 700,
          color: '#1e3a5f',
          lineHeight: 1,
          position: 'relative',
          zIndex: 1,
        }}>
          {omerDayHebrew}
        </Typography>
      </Box>

      {/* שורת ספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.85rem, 2.8vw, 1.2rem)',
        color: '#1e293b',
        mt: 0.5,
        textAlign: 'center',
        direction: 'rtl',
        position: 'relative',
      }}>
        {mainCountText}
      </Typography>

      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
          color: '#64748b',
          mt: 0.25,
          textAlign: 'center',
          direction: 'rtl',
          position: 'relative',
        }}>
          {subCountText}
        </Typography>
      )}

      <Typography sx={{
        fontSize: '0.62rem',
        color: '#94a3b8',
        mt: 0.75,
        fontStyle: 'italic',
        fontFamily: 'Frank Ruhl Libre, serif',
        position: 'relative',
      }}>
        {sefirahName}
      </Typography>

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 420, position: 'relative' }}>
          {afterCountSections.map((section) => (
            <AfterCountSection key={section.id} section={section} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Modern;
